import React, { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import API from '../api/api';
import { toast } from 'react-toastify';
import { RiDeleteBinFill } from "react-icons/ri";
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';




const SurveyCenter = () => {
  const { jobId, runId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('quality');
  const [unlockedTabs, setUnlockedTabs] = useState(['quality']);
  const [qualityCheckData, setQualityCheckData] = useState(location.state?.qualityCheckData || null);
  const [loading, setLoading] = useState(false);
  const [isInterpolated, setIsInterpolated] = useState(false);
  const [surveyCompleted, setSurveyCompleted] = useState(false);

  


  useEffect(() => {
    if (!qualityCheckData && jobId && runId) {
      fetchQualityCheckData();
    }
  }, [qualityCheckData, jobId, runId]);

  const checkSurveyExists = async () => {
    try {
      const response = await API.get(`survey-exists/${runId}/`);
      setSurveyCompleted(response.data.exists);
    } catch (error) {
      console.error('Error checking survey status:', error);
    }
  };

  const fetchQualityCheckData = async () => {
    try {
      setLoading(true);
      const response = await API.get(`/api/upload-quality-check/${jobId}/${runId}/`);
      setQualityCheckData(response.data);
    } catch (error) {
      console.error('Failed to fetch quality check data', error);
      toast.error('Could not load quality check data');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { key: 'quality', label: 'Quality Check' },
    { key: 'survey', label: 'Survey Calculation' },
    { key: 'comparison', label: 'Comparison' },
  ];

  const handleBack = () => {
    console.log(jobId)
    navigate(`/jobdetails/${jobId}?tab=runs`);
  };
  

  return (
    <div className="max-w-9xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={handleBack}
          className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition"
        >
          ← Back to Job Details
        </button>
        
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-300 dark:border-gray-600 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => unlockedTabs.includes(tab.key) && setActiveTab(tab.key)}
            className={`px-4 py-2 font-semibold transition-colors duration-200 ${
              activeTab === tab.key
                ? 'border-b-2 border-red-600 text-red-600'
                : unlockedTabs.includes(tab.key)
                ? 'text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400'
                : 'text-gray-400 cursor-not-allowed'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="bg-white dark:bg-gray-800 p-6 border border-gray-300 dark:border-gray-700 rounded-lg shadow">
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : (
          <>
            {activeTab === 'quality' && (
              <QualityCheckTab
                data={qualityCheckData}
                jobId={jobId}
                runId={runId}
                onNext={(interpolated) => {
                  setIsInterpolated(interpolated);
                  setSurveyCompleted(true);
                  if (!unlockedTabs.includes('survey')) {
                    setUnlockedTabs((prev) => [...prev, 'survey']);
                  }
                  setActiveTab('survey');
                }}
                surveyCompleted={surveyCompleted}
              />
            )}
            {activeTab === 'survey' && (
              <SurveyCalculationTab
                onNext={() => {
                  if (!unlockedTabs.includes('comparison')) {
                    setUnlockedTabs((prev) => [...prev, 'comparison']);
                  }
                  setActiveTab('comparison');
                }}
                isInterpolated={isInterpolated}
                setIsInterpolated={setIsInterpolated}
              />
            )}
            {activeTab === 'comparison' && <ComparisonTab />}
          </>
        )}
      </div>
    </div>
  );
};

// -----------------------------------------
// Quality Check Tab
// -----------------------------------------
 
const QualityCheckTab = ({ data,jobId, runId, onNext ,surveyCompleted}) => {
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteStationNumber, setConfirmDeleteStationNumber] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showInterpolationPrompt, setShowInterpolationPrompt] = useState(false);
  const [interpolationStep, setInterpolationStep] = useState('choice'); // 'choice' | 'inputs'
  const [resolution, setResolution] = useState('');
  const [fromDepth, setFromDepth] = useState('');
  const [toDepth, setToDepth] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [localData, setLocalData] = useState(data);
  const [fullSurvey, setFullSurvey] = useState(false);
  

  const getGTStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'high':
        return 'bg-green-600 text-gray-800';
      case 'good':
        return 'bg-green-500 text-gray-800';
      case 'low':
        return 'bg-green-300 text-gray-800';
      default:
        return '';
    }
  };

  const getWTStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'high':
        return 'bg-green-600 text-gray-800';
      case 'good':
        return 'bg-green-400 text-gray-800';
      case 'low':
        return 'bg-green-200 text-gray-800';
      default:
        return '';
    }
  };
    const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pass':
        return 'bg-green-600 text-gray-800';
      case 'good':
        return 'bg-green-400 text-gray-800';
      case 'low':
        return 'bg-green-200 text-gray-800';
      default:
        return '';
    }
  };
  useEffect(() => {
    setLocalData(data); // Sync prop into local state on mount or prop change
  }, [data]);

 const handleDelete = async () => {
    if (!confirmDelete || !jobId || !runId ||  surveyCompleted) return;

    setIsDeleting(true);
    try {
      await API.delete(`api/upload-quality-check/${jobId}/${runId}/`, {
        data: { depth: confirmDelete.depth },
      });

      toast.success(`Station #${confirmDeleteStationNumber} at depth ${confirmDelete.depth} deleted successfully.`);

      setLocalData((prevData) => ({
        ...prevData,
        results: prevData.results.filter((row) => row.depth !== confirmDelete.depth),
      }));
    } catch (error) {
      console.error('Failed to delete station', error);
      toast.error('Failed to delete station.');
    } finally {
      setIsDeleting(false);
      setConfirmDelete(null);
      setConfirmDeleteStationNumber(null);
    }
  };
const handleNextClick = async () => {
  setIsProcessing(true);
  try {
    // 1. First check if interpolated data exists
    try {
      const interpolatedResponse = await API.get(`survey-interpolate/${runId}/`, {
        // params: { run_id: runId }
      });
      
      if (interpolatedResponse?.data?.data?.length > 0) {
        toast.success('Showing interpolated survey');
        onNext(true); // Mark as interpolated
        return;
      }
    } catch (interpolatedError) {
      console.log('No interpolated data found, checking for calculated survey');
    }

    // 2. Only check for calculated survey if no interpolated data exists
    try {
      const calculatedResponse = await API.get(`runs/calculate-survey/${runId}/`);
      if (calculatedResponse?.data?.details?.length > 0) {
        toast.success('Showing calculated survey');
        onNext(false);
        return;
      }
    } catch (calculatedError) {
      console.log('No calculated survey found');
    }

    // 3. If neither exists, show interpolation prompt
    setShowInterpolationPrompt(true);
    setInterpolationStep('choice');
    
  } catch (err) {
    console.error(err);
    toast.error('Failed to check survey data');
  } finally {
    setIsProcessing(false);
  }
};

  const handleInterpolationYes = async () => {
  setIsProcessing(true);
  try {
    // First, calculate the original survey
    await API.post(`/runs/calculate-survey/${runId}/`);
    toast.success('Original survey calculated successfully');
    // Then show interpolation inputs
    setInterpolationStep('inputs');
  } catch (error) {
    console.error(error);
    toast.error('Failed to calculate original survey');
  } finally {
    setIsProcessing(false);
  }
};

  const handleInterpolationSubmit = async () => {
  if (!resolution) {
    toast.error('Please enter resolution value');
    return;
  }

  // Validate depth range only if not doing full survey
  if (!fullSurvey) {
    if (!fromDepth || !toDepth) {
      toast.error('Please enter both From and To depths');
      return;
    }
    if (Number(fromDepth) >= Number(toDepth)) {
      toast.error('"From Depth" must be less than "To Depth"');
      return;
    }
  }

  setIsProcessing(true);
  try {
    const response = await API.post(`survey-interpolate/${runId}/`, {
      resolution: Number(resolution),
      survey_from: fullSurvey ? null : Number(fromDepth),
      survey_to: fullSurvey ? null : Number(toDepth),
      full_survey: fullSurvey
    });

    if (response.status === 201 && response.data?.data) {
      toast.success(`Survey ${fullSurvey ? 'fully' : 'partially'} interpolated`);
      onNext(true);
    } else {
      throw new Error(response.data?.message || 'Interpolation failed');
    }
  } catch (error) {
    console.error('Interpolation error:', error);
    toast.error(error.message);
  } finally {
    setIsProcessing(false);
    setShowInterpolationPrompt(false);
    setResolution('');
    setFromDepth('');
    setToDepth('');
    setFullSurvey(false);
  }
};

  const handleInterpolationNo = async () => {
    setIsProcessing(true);
    try {
      await API.post(`/runs/calculate-survey/${runId}/`);
      toast.success('Survey calculation started');
      onNext(false);
    } catch (error) {
      console.error(error);
      toast.error('Survey calculation failed');
    } finally {
      setIsProcessing(false);
      setShowInterpolationPrompt(false);
    }
  };


  if (!localData) {
    return <p className="text-gray-500">No quality check data available.</p>;
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap justify-between items-start gap-4 text-sm">
        <div className="space-y-1">
          <p className="text-xs sm:text-sm">
            <strong>G(t) Score:</strong> {data.summary?.g_t_score} ({data.summary?.g_t_percentage})
          </p>
          <p className="text-xs sm:text-sm">
            <strong>W(t) Score:</strong> {data.summary?.w_t_score} ({data.summary?.w_t_percentage})
          </p>
        </div>
        <div className="space-y-1 text-right">
          <p className="text-xs sm:text-sm">
            <strong>Uploaded File Name:</strong> {data.summary?.file_name}
          </p>
          <p className="text-xs sm:text-sm">
            <strong>Success Count:</strong> {data.summary?.success_count}
          </p>
        </div>
      </div>

      <div className="border border-gray-300 dark:border-gray-600 rounded-lg">
        <div className="overflow-x-auto max-h-[600px]">
          <table className="min-w-full text-xs sm:text-sm text-gray-900 dark:text-gray-100">
            <thead className="sticky top-0 bg-gray-100 dark:bg-gray-700 z-10">
              <tr>
                <th className="p-2 border w-[80px]">Survey Station</th>
                <th className="p-2 border">Depth</th>
                <th className="p-2 border">INC</th>
                <th className="p-2 border">AZG</th>
                <th className="p-2 border">G(t)</th>
                <th className="p-2 border">ΔTGF</th>
                <th className="p-2 border w-[100px]">ΔTGF Comp</th>
                <th className="p-2 border">W(t)</th>
                <th className="p-2 border">ΔW(t)</th>
                <th className="p-2 border w-[100px]">ΔW(t) Comp</th>
                <th className="p-2 border w-[100px]">Status</th>
                <th className="p-2 border w-[100px]">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 dark:hover:bg-gray-700">
              {localData?.results?.map((row, idx) => (
                <tr key={idx}>
                  <td className="p-2 border">{idx + 1}</td>
                  <td className="p-2 border">{row.depth}</td>
                  <td className="p-2 border">{row.inc}</td>
                  <td className="p-2 border">{row.azg}</td>
                  <td className="p-2 border">{row.gt}</td>
                  <td className="p-2 border">{row.g_t_difference}</td>
                  <td className={`p-2 border ${getGTStatusColor(row.g_t_status)}`}>
                    {row.g_t_status}
                  </td>
                  <td className="p-2 border">{row.wt}</td>
                  <td className="p-2 border">{row.w_t_difference}</td>
                  <td className={`p-2 border ${getWTStatusColor(row.w_t_status)}`}>
                    {row.w_t_status}
                  </td>
                  <td className={`p-2 border ${getStatusColor(row.status)}`}>{row.status}</td>
                  <td className="p-2 border">
                    {surveyCompleted ? (
                      <RiDeleteBinFill className="text-gray-400 cursor-not-allowed" />
                    ) : (
                      <RiDeleteBinFill
                        className="text-red-600 hover:text-red-800 cursor-pointer"
                        onClick={() => {
                          setConfirmDelete(row);
                          setConfirmDeleteStationNumber(idx + 1);
                        }}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={handleNextClick}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Next
        </button>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-sm w-full">
            <h2 className="text-lg font-bold mb-4 text-red-600">Confirm Delete</h2>
            <p className="mb-4">
              Are you sure you want to delete Station<strong> #{confirmDeleteStationNumber}</strong> at depth <strong>{confirmDelete.depth}</strong>?
            </p>
            <div className="flex justify-end space-x-2">
              <button
                className="px-4 py-2 rounded bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500"
                onClick={() => setConfirmDelete(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                onClick={() => handleDelete(confirmDelete)}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showInterpolationPrompt && (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-sm w-full">
      {interpolationStep === 'choice' && (
        <>
          <h2 className="text-lg font-bold mb-4 text-red-600">Interpolate Survey?</h2>
          <p className="mb-4">Do you want to interpolate the survey?</p>
          <div className="flex justify-end space-x-2">
            <button
              className="px-4 py-2 rounded bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500"
              onClick={() => setShowInterpolationPrompt(false)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
              onClick={handleInterpolationYes}
            >
              Yes
            </button>
            <button
              className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
              onClick={handleInterpolationNo}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'No'}
            </button>
          </div>
        </>
      )}

      {interpolationStep === 'inputs' && (
        <>
          <h2 className="text-lg font-bold mb-4 text-red-600">Interpolation Parameters</h2>
          <div className="space-y-4">
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={fullSurvey}
                  onChange={(e) => {
                    setFullSurvey(e.target.checked);
                    if (e.target.checked) {
                      setFromDepth('');
                      setToDepth('');
                    }
                  }}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <span className="text-gray-700 dark:text-gray-300">Interpolate Full Survey</span>
              </label>
            </div>

            <input
              type="number"
              className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
              placeholder="Resolution *"
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              required
            />

            {!fullSurvey && (
              <>
                <input
                  type="number"
                  className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                  placeholder="From Depth *"
                  value={fromDepth}
                  onChange={(e) => setFromDepth(e.target.value)}
                  required
                />
                <input
                  type="number"
                  className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                  placeholder="To Depth *"
                  value={toDepth}
                  onChange={(e) => setToDepth(e.target.value)}
                  required
                />
              </>
            )}
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              className="px-4 py-2 rounded bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500"
              onClick={() => setShowInterpolationPrompt(false)}
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              onClick={handleInterpolationSubmit}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Submit'}
            </button>
          </div>
        </>
      )}
    </div>
  </div>
)}

    </div>
  );
};

// -----------------------------------------
// Survey Calculation Tab
// -----------------------------------------

const SurveyCalculationTab = ({ onNext, isInterpolated }) => {
  const { runId } = useParams();
  const [surveyData, setSurveyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (runId) {
      fetchSurveyData();
    }
  }, [runId, isInterpolated]);

  const fetchSurveyData = async () => {
  setLoading(true);
  setError('');
  try {
    const endpoint = isInterpolated 
      ? 'interpolate-survey/'
      : `runs/calculate-survey/${runId}/`;
    
    const params = isInterpolated ? { run_id: runId } : {};
    
    const response = await API.get(endpoint, { params });
    setSurveyData(response.data?.details || []);
    
  } catch (err) {
    console.error(err);
    setError('Failed to load survey data');
  } finally {
    setLoading(false);
  }
};

  // Helper function to get field values based on data type
  const getField = (row, field) => {
    if (isInterpolated) {
      // Field mapping for interpolated data
      const fieldMap = {
        latitude: 'ns',
        departure: 'ew',
        true_vertical_depth: 'tvd',
        Vertical_Section: 'vertical_section',
        DLS: 'dls',
        CL: 'cl'
      };
      return row[fieldMap[field] || field];
    }
    // For calculated survey, use direct field names
    return row[field];
  };

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-xl font-bold text-red-500">
          {isInterpolated ? 'Interpolated Survey Results' : 'Survey Calculation Results'}
        </h2>
        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Data source: {isInterpolated ? 'Interpolated values' : 'Original survey calculations'}
        </div>
      </div>

      {/* Loading and error states */}
      {loading && <p className="text-gray-500">Loading survey data...</p>}
      {error && <p className="text-red-600">{error}</p>}

      {/* Data display */}
      {!loading && !error && surveyData.length === 0 && (
        <p className="text-gray-500">No survey data available.</p>
      )}

      {!loading && !error && surveyData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_3fr] md:grid-cols-[1fr_3fr] gap-2">
          {/* Graphs section */}
          <div className="flex flex-col gap-4">
            {/* Graph 1: +N/-S vs +E/-W */}
            <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-2 shadow">
              <h3 className="text-lg font-bold mb-2 text-gray-500">Graph: +N/-S vs +E/-W</h3>
              <ResponsiveContainer width="100%" height={250}>
                <ScatterChart>
                  <CartesianGrid />
                  <XAxis
                    dataKey={isInterpolated ? "ns" : "latitude"}
                    name="+N/-S"
                    unit="m"
                    label={{ value: 'Latitude', position: 'insideBottom', offset: -20 }}
                  />
                  <YAxis
                    dataKey={isInterpolated ? "ew" : "departure"}
                    name="+E/-W"
                    unit="m"
                    label={{ value: 'Departure', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                  <Legend />
                  <Scatter data={surveyData} fill="#ef4444" line shape={() => null} />
                </ScatterChart>
              </ResponsiveContainer>
            </div>

            {/* Graph 2: Vertical Section vs TVD */}
            <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-2 shadow">
              <h3 className="text-lg font-bold mb-2 text-gray-500">Graph: Vertical Section vs TVD</h3>
              <ResponsiveContainer width="100%" height={250}>
                <ScatterChart>
                  <CartesianGrid />
                  <XAxis
                    dataKey={isInterpolated ? "tvd" : "true_vertical_depth"}
                    name="TVD"
                    unit="m"
                    label={{ value: 'TVD', position: 'insideBottom', offset: -20 }}
                  />
                  <YAxis
                    dataKey={isInterpolated ? "vertical_section" : "Vertical_Section"}
                    name="Vertical Section"
                    unit="m"
                    label={{ value: 'Vertical Section', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                  <Legend />
                  <Scatter data={surveyData} fill="#3b82f6" line shape={() => null} />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Table section */}
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-x-auto max-h-[600px]">
            <table className="w-full text-xs sm:text-sm text-gray-900 dark:text-gray-100">
              <thead className="sticky top-0 bg-gray-100 dark:bg-gray-700 z-10 text-gray">
                <tr>
                  <th className="p-2 border">#</th>
                  <th className="p-2 border">Depth</th>
                  <th className="p-2 border">Inclination</th>
                  <th className="p-2 border">Azimuth</th>
                  <th className="p-2 border">+N/-S</th>
                  <th className="p-2 border">+E/-W</th>
                  <th className="p-2 border">TVD</th>
                  <th className="p-2 border">Vertical Section</th>
                  <th className="p-2 border">Closure Distance</th>
                  <th className="p-2 border">Closure Direction</th>
                  <th className="p-2 border">DLS</th>
                  <th className="p-2 border">Dog leg</th>
                  <th className="p-2 border">Ratio Factor</th>
                  {isInterpolated && <th className="p-2 border">Original</th>}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800">
                {surveyData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="p-2 border">{idx + 1}</td>
                    <td className="p-2 border">{row.depth}</td>
                    <td className="p-2 border">{row.inclination}</td>
                    <td className="p-2 border">{row.azimuth}</td>
                    <td className="p-2 border">{getField(row, 'latitude')}</td>
                    <td className="p-2 border">{getField(row, 'departure')}</td>
                    <td className="p-2 border">{getField(row, 'true_vertical_depth')}</td>
                    <td className="p-2 border">{getField(row, 'Vertical_Section')}</td>
                    <td className="p-2 border">{row.closure_distance}</td>
                    <td className="p-2 border">{row.closure_direction}</td>
                    <td className="p-2 border">{getField(row, 'DLS')}</td>
                    <td className="p-2 border">{row.dog_leg}</td>
                    <td className="p-2 border">{row.ratio_factor}</td>
                    {isInterpolated && (
                      <td className="p-2 border">{row.is_original ? 'Yes' : 'No'}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <button
          onClick={onNext}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Next
        </button>
      </div>
    </div>
  );
};
// -----------------------------------------
// Comparison Tab
// -----------------------------------------

const ComparisonTab = () => {
  const { runId } = useParams();
  const [runInfo, setRunInfo] = useState(null);
  const [comparisonType, setComparisonType] = useState('');
  const [externalFile, setExternalFile] = useState(null);
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchRunInfo();
  }, [runId]);

  const fetchRunInfo = async () => {
    const response = await API.get(`runs/${runId}/`);
    setRunInfo(response.data);
    console.log('Run Info:', response.data);
    
    // Set default comparison type based on survey_type_id
    const surveyTypeId = response.data.survey_type;
    console.log('Survey Type ID:', surveyTypeId);
    if (surveyTypeId === 3) {
      setComparisonType('inrun-outrun');
    } else if (surveyTypeId === 1) {
      setComparisonType('inrun-external');
    } else if (surveyTypeId === 2) {
      setComparisonType('outrun-external');
    }
  };

  const handleFileUpload = (e) => {
    setExternalFile(e.target.files[0]);
  };

  const runComparison = async () => {
  setIsLoading(true);
  try {
    const formData = new FormData();
    formData.append('comparison_type', comparisonType);
    if (externalFile) {
      formData.append('external_file', externalFile);
    }

    const response = await API.post(`survey-comparison/${runId}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    // Get full comparison results with details
    const resultsResponse = await API.get(`comparison-results/${response.data.id}/`);
    setResults(resultsResponse.data.results);
  } catch (error) {
    console.error('Comparison failed:', error);
  } finally {
    setIsLoading(false);
  }
};

  // Get the survey type ID safely
 const surveyTypeId = runInfo?.survey_type;
    
    console.log('Final surveyTypeId:', surveyTypeId);

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Survey Comparison</h2>
      
      <div className="mb-4">
        <label className="block mb-2">Comparison Type:</label>
        <select 
          value={comparisonType}
          onChange={(e) => setComparisonType(e.target.value)}
          className="p-2 border rounded w-full"
          disabled={!runInfo}
        >
          <option value="">Select comparison type</option>
          {surveyTypeId === 3 && (
            <option value="inrun-outrun">Compare Inrun vs Outrun</option>
          )}
          {surveyTypeId === 1 && (
            <option value="inrun-external">Compare Inrun vs External</option>
          )}
          {surveyTypeId === 2 && (
            <option value="outrun-external">Compare Outrun vs External</option>
          )}
        </select>
      </div>


      {(comparisonType === 'inrun-external' || comparisonType === 'outrun-external') && (
        <div className="mb-4">
          <label className="block mb-2">Upload External Survey File:</label>
          <input 
            type="file" 
            onChange={handleFileUpload}
            className="p-2 border rounded"
          />
        </div>
      )}

      <button
        onClick={runComparison}
        disabled={isLoading || (comparisonType.includes('external') && !externalFile)}
        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
      >
        {isLoading ? 'Comparing...' : 'Run Comparison'}
      </button>

      {results.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-bold mb-2">Comparison Results</h3>
          <table className="min-w-full border">
            <thead>
              <tr>
                <th className="p-2 border">Depth</th>
                <th className="p-2 border">TVD Diff</th>
                <th className="p-2 border">N/S Diff</th>
                <th className="p-2 border">E/W Diff</th>
                <th className="p-2 border">Inclination Diff</th>
                <th className="p-2 border">Azimuth Diff</th>
                <th className="p-2 border">DLS Diff</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result, index) => (
                <tr key={index}>
                  <td className="p-2 border">{result.depth}</td>
                  <td className="p-2 border">{result.tvd_difference}</td>
                  <td className="p-2 border">{result.ns_difference}</td>
                  <td className="p-2 border">{result.ew_difference}</td>
                  <td className="p-2 border">{result.inclination_difference}</td>
                  <td className="p-2 border">{result.azimuth_difference}</td>
                  <td className="p-2 border">{result.dls_difference || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
export default SurveyCenter;
